import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PopupLoginError from '../../../components/Popup/LoginError';
import { validatePassword, validatePasswordMatch } from '../../../utils/validation';
import useIdentityVerification from '../../../hooks/useIdentityVerification';
import usePopupStore from '../../../store/popupStore';
import { PASSWORD_RESET_URL } from '../../../utils/urls';
import instance from '../../../services/services';
import { useLanguage } from '../../../hooks/useLanguage';

function PasswordFind() {
    const { t } = useLanguage();
    const [userId, setUserId] = useState('');
    const [isPopupLoginError, setIsPopupLoginError] = useState(""); // 에러 팝업
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [passwordValidError, setPasswordValidError] = useState('');
    const [isFormValid, setIsFormValid] = useState(false); // 폼 유효성 상태 추가
    const [isCompleted, setIsCompleted] = useState(false); // 비밀번호 변경 완료 상태 추가
    
    const { showAlert } = usePopupStore();

    const { handleIdentity } = useIdentityVerification({
        context: 'PasswordFind',
        extraData: { userId }  // userId 전달
    });

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const verified = params.get('verified');
        
        if (verified === 'true') {
            // localStorage에서 인증 정보 가져오기
            const authInfoStr = localStorage.getItem('authVerificationInfo');
            if (authInfoStr) {
                const authInfo = JSON.parse(authInfoStr);
                // 본인인증 성공 상태로 변경
                setIsAuthenticated(true);
                // userId 복원
                if (authInfo.userId) {
                    setUserId(authInfo.userId);
                }
                // 사용 후 삭제
                localStorage.removeItem('authVerificationInfo');
            }
        }
    }, []);

    const handleAuth = () => {
        if (!userId) {
            setIsPopupLoginError('아이디를 입력해주세요.');
            return;
        }
        // 본인인증 실행
        handleIdentity();
    };

    // 폼 유효성 검사 함수
    const checkFormValidity = (password, confirmPass) => {
        // 두 입력값이 모두 존재하고
        if (password && confirmPass) {
            // 비밀번호가 유효성 검사를 통과하고
            const isPasswordValid = validatePassword(password);
            // 비밀번호 확인이 일치하면
            const isPasswordMatch = validatePasswordMatch(password, confirmPass);
            
            setIsFormValid(isPasswordValid && isPasswordMatch);
        } else {
            setIsFormValid(false);
        }
    };

    const handlePasswordChange = (e) => {
        const newPass = e.target.value;
        setNewPassword(newPass);
        
        // 비밀번호 유효성 검사
        if (newPass && !validatePassword(newPass)) {
            setPasswordValidError('영문 대소문자/숫자/특수문자 중 2가지 이상 조합, 8~16자로 입력해 주세요.');
        } else {
            setPasswordValidError('');
        }

        // 비밀번호 확인란이 이미 입력되어 있다면 일치 여부 검사
        if (confirmPassword) {
            if (!validatePasswordMatch(newPass, confirmPassword)) {
                setPasswordError('비밀번호가 일치하지 않습니다.');
            } else {
                setPasswordError('');
            }
        }

        // 폼 전체 유효성 검사
        checkFormValidity(newPass, confirmPassword);
    };

    const handleConfirmPasswordChange = (e) => {
        const confirmPass = e.target.value;
        setConfirmPassword(confirmPass);
        
        // 비밀번호 일치 여부 검사
        if (confirmPass && !validatePasswordMatch(newPassword, confirmPass)) {
            setPasswordError('비밀번호가 일치하지 않습니다.');
        } else {
            setPasswordError('');
        }

        // 폼 전체 유효성 검사
        checkFormValidity(newPassword, confirmPass);
    };

    // 확인 버튼 클릭 핸들러 수정
    const handleSubmit = async () => {
        try {
            // API 요청 데이터 구성
            const requestData = {
                userid: userId,            // 처음에 입력한 아이디
                password: newPassword,     // 새로 입력한 비밀번호
                repassword: confirmPassword // 비밀번호 확인
            };

            // 비밀번호 재설정 API 호출
            const response = await instance.post(PASSWORD_RESET_URL, requestData);

            if (response.status === 200) {
                // 성공 시 완료 상태로 변경
                setIsCompleted(true);
            } else {
                // 실패 시 에러 메시지 표시
                showAlert({
                    text: "비밀번호 변경에 실패했습니다. 다시 시도해주세요.",
                    closeButton: "확인"
                });
            }
        } catch (error) {
            console.error('비밀번호 변경 중 오류:', error);
            showAlert({
                text: "비밀번호 변경 중 오류가 발생했습니다.",
                closeButton: "확인"
            });
        }
    };

    return (
        <section className="auth_section">
            <div className="inner">
                <div className="section_title">
                    <h3>{t("account_find")}</h3>
                </div>
                
                <div className="find_wrap">
                    <div className="tab_menu">
                        <Link to="/auth/find/id" className="tab_item">{t("id")}</Link>
                        <Link to="/auth/find/password" className="tab_item active">{t("password")}</Link>
                    </div>
                    
                    {!isAuthenticated ? (
                        // 초기 화면 - 아이디 입력 및 본인인증
                        <div className="find_content">
                            <p className="accession_text">{t("enter_id")}</p>
                            <p className="sub_guide_text" style={{marginBottom:"32px"}}>{t("change_password_by_verification")}</p>
                            
                            <div className="form_group2">
                                <div className="input_wrap">
                                    <label>{t("id")}</label>
                                    <div className="default_input lg">
                                        <input 
                                            type="text" 
                                            placeholder={t("enter_id_instruction")} 
                                            value={userId}
                                            onChange={(e) => setUserId(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="auth_btn_wrap">
                                    <button 
                                        type="button" 
                                        className="default_btn black lg w-100"
                                        onClick={handleAuth}
                                    >
                                        {t("verify_identity")}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : isCompleted ? (
                        // 비밀번호 변경 완료 화면
                        <div className="find_content">
                            <p className="accession_text">{t("password_change_completed")}</p>
                            
                            <div className="password_change_steps" style={{marginTop:"32px"}}>
                                <div className="step_wrap">
                                    <button 
                                        type="button" 
                                        className="default_btn black lg w-100"
                                        onClick={() => window.location.href = '/login'}
                                    >
                                        {t("go_to_login")}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        // 비밀번호 변경 화면
                        <div className="find_content">
                            <p className="accession_text">{t("password_change_completed2_0617")}<br />{t("password_change_completed3_0617")}</p>
                            
                            <div className="password_change_steps" style={{marginTop:"32px"}}>
                                {/* 새 비밀번호 입력 */}
                                <div className="step_wrap">
                                    <div className="step_content">
                                        <div className="input_wrap">
                                            <label>{t("new_password")}</label>
                                            <div className="default_input lg">
                                                <input 
                                                    type="password" 
                                                    placeholder={t("new_password_0617")} 
                                                    value={newPassword}
                                                    onChange={handlePasswordChange}
                                                />
                                            </div>
                                        </div>
                                        {passwordValidError && newPassword && (
                                            <p className="guide_text red">{passwordValidError}</p>
                                        )}
                                    </div>
                                </div>

                                {/* 새 비밀번호 확인 */}
                                <div className="step_wrap" style={{marginTop:"16px"}}>
                                    <div className="step_content">
                                        <div className="input_wrap">
                                            <label>{t("new_password_confirm")}</label>
                                            <div className="default_input lg">
                                                <input 
                                                    type="password" 
                                                    placeholder={t("reenter_new_password")} 
                                                    value={confirmPassword}
                                                    onChange={handleConfirmPasswordChange}
                                                />
                                            </div>
                                        </div>
                                        {passwordError && confirmPassword && (
                                            <p className="guide_text red">{passwordError}</p>
                                        )}
                                    </div>
                                </div>

                                <div className="step_wrap" style={{marginTop:"32px"}}>
                                    <button 
                                        type="button" 
                                        className={`default_btn ${isFormValid ? 'black' : 'gray2'} lg w-100`}
                                        onClick={handleSubmit}
                                        disabled={!isFormValid}
                                    >
                                        {t("confirm")}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                {/* NICE 인증을 위한 숨겨진 폼 */}
                <form 
                    id="niceAuthForm" 
                    name="niceAuthForm" 
                    action="https://nice.checkplus.co.kr/CheckPlusSafeModel/checkplus.cb"
                    method="post"
                    style={{ display: 'none' }}
                >
                    <input type="hidden" id="m" name="m" value="service" />
                    <input type="hidden" id="token_version_id" name="token_version_id" value="" />
                    <input type="hidden" id="enc_data" name="enc_data" value="" />
                    <input type="hidden" id="integrity_value" name="integrity_value" value="" />
                    <input type="hidden" id="receivedata" name="receivedata" value="" />
                </form>
            </div>
            <PopupLoginError isPopup={isPopupLoginError} setIsPopup={setIsPopupLoginError}/>
        </section>
    );
}

export default PasswordFind;